/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file MailCollabSheets.js
 * @description
 * Premium bottom sheets of shared-mailbox collaboration, all built on the app's
 * {@link PremiumSheet} (keyboard-aware, dark-mode aware):
 *
 *  - {@link AssignSheet}  member picker (avatar, name, online dot, workload, search,
 *    "Assign to me", "Unassign").
 *  - {@link TagsSheet}    multi-select of team tags (shared mailbox) + personal tags (mailbox
 *    labels) with inline create.
 *  - {@link SnoozeSheet}  "Notify again later": server-resolved presets plus an in-N-hours stepper
 *    (the app ships no date-time picker that works on web + native, so no dependency is added).
 *
 * The sheets are presentational: every mutation is delegated to the caller through callbacks.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ActivityIndicator, StyleSheet, Switch, Text, TextInput, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../../../components/management/SafeIcon';
import {PremiumSheet} from '../../../components/settings/SettingsUiKit';
import {useManagementTheme} from '../../../helpers/managementTheme';
import {CollabAvatar, CollabSheetRow} from '../../../components/mail/MailCollabParts';
import {SNOOZE_PRESETS, TAG_COLORS, userId} from '../../../helpers/mailCollabUtils';

/** Fallback hour steps of the snooze stepper when the mailbox defines none. */
const DEFAULT_SNOOZE_HOURS = [1, 2, 3, 4, 6, 8, 12, 24, 48, 72, 168];

/**
 * Compact primary / secondary action button used inside the sheets.
 *
 * @param {{label: string, icon?: string, onPress: () => void, primary?: boolean, disabled?: boolean, danger?: boolean}} props Props.
 * @returns {JSX.Element} Button.
 */
const SheetButton = ({label, icon, onPress, primary, disabled, danger}) => {
    const m = useManagementTheme();
    const fg = primary ? '#FFFFFF' : (danger ? m.colors.danger : m.colors.heading);

    return (
        <TouchableOpacity
            onPress={onPress}
            disabled={disabled}
            activeOpacity={0.85}
            style={[
                styles.btn,
                primary
                    ? {backgroundColor: m.colors.primary}
                    : {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border, borderWidth: StyleSheet.hairlineWidth},
                disabled && {opacity: 0.5},
            ]}
        >
            {!!icon && <Icon name={icon} size={18} color={fg} />}
            <Text style={[styles.btnText, {color: fg}]}>{label}</Text>
        </TouchableOpacity>
    );
};

/**
 * Small themed text input.
 *
 * @param {Object} props TextInput props plus `icon`.
 * @returns {JSX.Element} Input.
 */
const SheetInput = ({icon, style, ...rest}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.inputWrap, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}, style]}>
            {!!icon && <Icon name={icon} size={17} color={m.colors.muted} />}
            <TextInput
                {...rest}
                placeholderTextColor={m.colors.muted}
                style={[styles.input, {color: m.colors.heading}]}
                autoCorrect={false}
            />
        </View>
    );
};

/**
 * Member picker sheet for assigning a thread.
 *
 * @param {Object} props Props.
 * @param {boolean} props.visible Sheet visibility.
 * @param {() => void} props.onClose Close handler.
 * @param {string} props.address Shared mailbox address.
 * @param {Object|null} props.state Current thread state (marks the current assignee).
 * @param {string} props.meId Signed-in user id.
 * @param {(address: string) => Promise<Object[]>} props.fetchMembers Members loader.
 * @param {(member: Object, members: Object[]) => void} props.onAssign Called with the picked member.
 * @param {() => void} props.onUnassign Unassign handler.
 * @param {(members: Object[]) => void} [props.onMembers] Receives the loaded members (for the takeover copy).
 * @returns {JSX.Element} Sheet.
 */
export const AssignSheet = ({visible, onClose, address, state, meId, fetchMembers, onAssign, onUnassign, onMembers}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!visible || !address) {
            return undefined;
        }
        let cancelled = false;

        setLoading(true);
        setQuery('');
        fetchMembers(address)
            .then((list) => {
                if (!cancelled) {
                    setMembers(list);
                    if (onMembers) {
                        onMembers(list);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setMembers([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, address, fetchMembers]);

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();

        return members.filter((member) => !needle || String(member.name || '').toLowerCase().includes(needle));
    }, [members, query]);
    const currentId = userId(state?.assignee);
    const me = members.find((member) => userId(member) === meId) || null;

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('mail_collab_assign_title')}
            subtitle={t('mail_collab_assign_subtitle')}
            cancelText={t('cancel')}
        >
            <View style={styles.actionsRow}>
                <SheetButton
                    primary
                    icon="account-check-outline"
                    label={t('mail_collab_assign_me')}
                    disabled={!me && !meId}
                    onPress={() => onAssign(me || {_id: meId, name: ''}, members)}
                />
                {!!currentId && (
                    <SheetButton icon="account-off-outline" label={t('mail_collab_unassign')} onPress={onUnassign} />
                )}
            </View>
            <SheetInput icon="magnify" value={query} onChangeText={setQuery} placeholder={t('mail_collab_search_members')} />
            {loading ? (
                <ActivityIndicator style={styles.loader} color={m.colors.primary} />
            ) : filtered.length === 0 ? (
                <Text style={[styles.empty, {color: m.colors.muted}]}>{t('mail_collab_no_members')}</Text>
            ) : filtered.map((member, index) => (
                <CollabSheetRow
                    key={userId(member)}
                    leading={<CollabAvatar user={member} size={34} showOnline />}
                    title={member.name || '—'}
                    subtitle={t('mail_collab_open_count', {count: member.openAssignedCount || 0})}
                    selected={userId(member) === currentId}
                    last={index === filtered.length - 1}
                    onPress={() => onAssign(member, members)}
                />
            ))}
        </PremiumSheet>
    );
};

/**
 * Member picker sheet of the list filter "Assigned to ...": every teammate of the shared mailbox
 * (online first, with open workload), searchable, plus a shortcut to clear the person filter.
 *
 * @param {Object} props Props.
 * @param {boolean} props.visible Sheet visibility.
 * @param {() => void} props.onClose Close handler.
 * @param {string} props.address Shared mailbox address.
 * @param {string} props.meId Signed-in user id.
 * @param {string} [props.selectedId] Currently filtered teammate id.
 * @param {(address: string) => Promise<Object[]>} props.fetchMembers Members loader.
 * @param {(member: Object) => void} props.onPick Called with the picked member.
 * @param {() => void} props.onClear Clears the person filter.
 * @returns {JSX.Element} Sheet.
 */
export const PersonFilterSheet = ({visible, onClose, address, meId, selectedId, fetchMembers, onPick, onClear}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!visible || !address) {
            return undefined;
        }
        let cancelled = false;

        setLoading(true);
        setQuery('');
        fetchMembers(address)
            .then((list) => {
                if (!cancelled) {
                    setMembers(list);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setMembers([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [visible, address, fetchMembers]);

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();

        return members
            .filter((member) => !needle || String(member.name || '').toLowerCase().includes(needle))
            .sort((a, b) => (Number(Boolean(b.online)) - Number(Boolean(a.online))) || String(a.name || '').localeCompare(String(b.name || '')));
    }, [members, query]);

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('mail_collab_person_title')}
            subtitle={t('mail_collab_person_subtitle')}
            cancelText={t('cancel')}
        >
            {!!selectedId && (
                <View style={styles.actionsRow}>
                    <SheetButton icon="filter-remove-outline" label={t('mail_collab_person_clear')} onPress={onClear} />
                </View>
            )}
            <SheetInput icon="magnify" value={query} onChangeText={setQuery} placeholder={t('mail_collab_search_members')} />
            {loading ? (
                <ActivityIndicator style={styles.loader} color={m.colors.primary} />
            ) : filtered.length === 0 ? (
                <Text style={[styles.empty, {color: m.colors.muted}]}>{t('mail_collab_no_members')}</Text>
            ) : filtered.map((member, index) => (
                <CollabSheetRow
                    key={userId(member)}
                    leading={<CollabAvatar user={member} size={34} showOnline />}
                    title={userId(member) === meId ? `${member.name || '—'} · ${t('mail_collab_person_you')}` : (member.name || '—')}
                    subtitle={t('mail_collab_open_count', {count: member.openAssignedCount || 0})}
                    selected={userId(member) === selectedId}
                    last={index === filtered.length - 1}
                    onPress={() => onPick(member)}
                />
            ))}
        </PremiumSheet>
    );
};

/**
 * Colour swatch row used when creating a tag.
 *
 * @param {{value: string, onChange: (color: string) => void}} props Props.
 * @returns {JSX.Element} Swatches.
 */
export const ColorSwatches = ({value, onChange}) => {
    const m = useManagementTheme();

    return (
        <View style={styles.swatches}>
            {TAG_COLORS.map((color) => (
                <TouchableOpacity
                    key={color}
                    onPress={() => onChange(color)}
                    hitSlop={4}
                    style={[styles.swatch, {backgroundColor: color, borderColor: value === color ? m.colors.heading : 'transparent'}]}
                    accessibilityRole="button"
                />
            ))}
        </View>
    );
};

/**
 * Tag picker: team tags (multi-select, saved on "Save") + personal tags (toggled immediately,
 * they are mailbox labels) + inline create.
 *
 * @param {Object} props Props.
 * @param {boolean} props.visible Sheet visibility.
 * @param {() => void} props.onClose Close handler.
 * @param {Object[]} props.teamTags Team tags of the shared mailbox (`[]` for a personal mailbox).
 * @param {string[]} props.teamSelected Currently selected team tag ids.
 * @param {Object[]} props.personalTags Personal tags (labels).
 * @param {string[]} props.personalSelected Personal tag ids on the open message.
 * @param {boolean} props.canCreateTeam Caller may create team tags (manager).
 * @param {(ids: string[]) => Promise<void>} props.onSaveTeam Persists the team selection.
 * @param {(tagId: string, on: boolean) => Promise<void>} props.onTogglePersonal Toggles a personal tag.
 * @param {(scope: 'team'|'personal', name: string, color: string) => Promise<void>} props.onCreate Creates a tag.
 * @returns {JSX.Element} Sheet.
 */
export const TagsSheet = ({visible, onClose, teamTags, teamSelected, personalTags, personalSelected, canCreateTeam, onSaveTeam, onTogglePersonal, onCreate}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const [selected, setSelected] = useState(teamSelected || []);
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [color, setColor] = useState(TAG_COLORS[0]);
    const [scope, setScope] = useState(teamTags.length || canCreateTeam ? 'team' : 'personal');
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (visible) {
            setSelected(teamSelected || []);
            setName('');
        }
    }, [visible, teamSelected]);

    const toggle = useCallback((id) => {
        setSelected((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
    }, []);

    const save = useCallback(async () => {
        setSaving(true);
        try {
            await onSaveTeam(selected);
            onClose();
        } finally {
            setSaving(false);
        }
    }, [onSaveTeam, selected, onClose]);

    const create = useCallback(async () => {
        const trimmed = name.trim();

        if (!trimmed) {
            return;
        }
        setCreating(true);
        try {
            await onCreate(scope, trimmed, color);
            setName('');
        } finally {
            setCreating(false);
        }
    }, [name, scope, color, onCreate]);

    const dot = (tagColor) => <View style={[styles.tagDot, {backgroundColor: tagColor || m.colors.primary}]} />;

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('mail_collab_tags_title')}
            subtitle={t('mail_collab_tags_subtitle')}
            cancelText={t('cancel')}
        >
            {teamTags.length > 0 && (
                <>
                    <Text style={[styles.groupLabel, {color: m.colors.muted}]}>{t('mail_collab_team_tags')}</Text>
                    {teamTags.map((tag, index) => (
                        <CollabSheetRow
                            key={tag.id}
                            leading={dot(tag.color)}
                            title={tag.name}
                            selected={selected.includes(tag.id)}
                            last={index === teamTags.length - 1}
                            onPress={() => toggle(tag.id)}
                        />
                    ))}
                    <View style={styles.saveRow}>
                        <SheetButton primary icon={saving ? undefined : 'check'} label={t('mail_collab_save_tags')} disabled={saving} onPress={save} />
                    </View>
                </>
            )}
            {personalTags.length > 0 && (
                <>
                    <Text style={[styles.groupLabel, {color: m.colors.muted}]}>{t('mail_collab_personal_tags')}</Text>
                    {personalTags.map((tag, index) => (
                        <CollabSheetRow
                            key={tag.id}
                            leading={dot(tag.color)}
                            title={tag.name}
                            selected={personalSelected.includes(tag.id)}
                            last={index === personalTags.length - 1}
                            onPress={() => onTogglePersonal(tag.id, !personalSelected.includes(tag.id))}
                        />
                    ))}
                </>
            )}
            <Text style={[styles.groupLabel, {color: m.colors.muted}]}>{t('mail_collab_new_tag')}</Text>
            <SheetInput icon="tag-plus-outline" value={name} onChangeText={setName} placeholder={t('mail_collab_tag_name')} maxLength={40} />
            <ColorSwatches value={color} onChange={setColor} />
            {(teamTags.length > 0 || canCreateTeam) && (
                <View style={styles.scopeRow}>
                    {['team', 'personal'].map((option) => (
                        <TouchableOpacity
                            key={option}
                            onPress={() => setScope(option)}
                            disabled={option === 'team' && !canCreateTeam}
                            style={[
                                styles.scopeChip,
                                {borderColor: scope === option ? m.colors.primary : m.colors.border, backgroundColor: scope === option ? m.colors.chipBg : 'transparent'},
                                option === 'team' && !canCreateTeam && {opacity: 0.4},
                            ]}
                        >
                            <Text style={[styles.scopeText, {color: m.colors.heading}]}>
                                {option === 'team' ? t('mail_collab_scope_team') : t('mail_collab_scope_personal')}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </View>
            )}
            <View style={styles.saveRow}>
                <SheetButton icon="plus" label={t('mail_collab_create_tag')} disabled={creating || !name.trim()} onPress={create} />
            </View>
        </PremiumSheet>
    );
};

/**
 * "Notify again later" sheet.
 *
 * @param {Object} props Props.
 * @param {boolean} props.visible Sheet visibility.
 * @param {() => void} props.onClose Close handler.
 * @param {number[]} [props.hourSteps] Hour options of the stepper (mailbox `snoozePresets`).
 * @param {{until?: string}|null} [props.current] Pending snooze (offers "Cancel snooze").
 * @param {boolean} [props.outgoing] The conversation's newest mail is a SENT one (follow-up wording, toggle on by default).
 * @param {(request: {preset: string, hours?: number, onlyIfNoReply?: boolean}) => Promise<void>} props.onSnooze Creates the snooze.
 * @param {() => Promise<void>} [props.onCancel] Cancels the pending snooze.
 * @returns {JSX.Element} Sheet.
 */
export const SnoozeSheet = ({visible, onClose, hourSteps, current, onSnooze, onCancel, outgoing = false}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const steps = useMemo(() => {
        const list = (hourSteps || []).filter((value) => Number(value) > 0).map(Number);

        return list.length ? [...new Set(list)].sort((a, b) => a - b) : DEFAULT_SNOOZE_HOURS;
    }, [hourSteps]);
    const [index, setIndex] = useState(Math.min(2, steps.length - 1));
    const [busy, setBusy] = useState(false);
    // "Remind me only if nobody replied" - on by default for sent mail (a follow-up).
    const [onlyIfNoReply, setOnlyIfNoReply] = useState(Boolean(outgoing));

    useEffect(() => {
        if (visible) {
            setIndex(Math.min(2, steps.length - 1));
            setOnlyIfNoReply(Boolean(current?.onlyIfNoReply) || Boolean(outgoing));
        }
    }, [visible, steps, outgoing, current?.onlyIfNoReply]);

    const run = useCallback(async (request) => {
        setBusy(true);
        try {
            await onSnooze(onlyIfNoReply ? {...request, onlyIfNoReply: true} : request);
            onClose();
        } finally {
            setBusy(false);
        }
    }, [onSnooze, onClose, onlyIfNoReply]);

    const cancelCurrent = useCallback(async () => {
        setBusy(true);
        try {
            await onCancel();
            onClose();
        } finally {
            setBusy(false);
        }
    }, [onCancel, onClose]);

    const hours = steps[index] || steps[0];
    const label = hours % 24 === 0 && hours >= 24
        ? t('mail_collab_snooze_days', {count: hours / 24})
        : t('mail_collab_snooze_hours', {count: hours});

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t(outgoing ? 'mail_followup_title' : 'mail_collab_snooze_title')}
            subtitle={t(outgoing ? 'mail_followup_subtitle' : 'mail_collab_snooze_subtitle')}
            cancelText={t('cancel')}
        >
            {!!current && (
                <View style={[styles.currentBox, {backgroundColor: m.colors.chipBg, borderColor: m.colors.borderSoft}]}>
                    <Icon name="clock-outline" size={16} color={m.colors.primary} />
                    <Text style={[styles.currentText, {color: m.colors.heading}]} numberOfLines={1}>
                        {t('mail_collab_snoozed_until', {when: new Date(current.until).toLocaleString()})}
                    </Text>
                    {!!onCancel && (
                        <TouchableOpacity onPress={cancelCurrent} hitSlop={8}>
                            <Text style={[styles.cancelLink, {color: m.colors.danger}]}>{t('mail_collab_snooze_cancel')}</Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}
            <View style={[styles.toggleRow, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}]}>
                <Icon name="reply-outline" size={18} color={m.colors.primary} />
                <View style={styles.toggleText}>
                    <Text style={[styles.toggleTitle, {color: m.colors.heading}]}>{t('mail_snooze_if_no_reply')}</Text>
                    <Text style={[styles.toggleDesc, {color: m.colors.subText}]}>{t('mail_snooze_if_no_reply_desc')}</Text>
                </View>
                <Switch
                    value={onlyIfNoReply}
                    onValueChange={setOnlyIfNoReply}
                    disabled={busy}
                    trackColor={{true: m.colors.primary, false: m.colors.border}}
                    accessibilityLabel={t('mail_snooze_if_no_reply')}
                />
            </View>
            {SNOOZE_PRESETS.map((preset, i) => (
                <CollabSheetRow
                    key={preset}
                    leading={<Icon name="clock-outline" size={20} color={m.colors.primary} />}
                    title={t(`mail_collab_snooze_${preset}`)}
                    last={i === SNOOZE_PRESETS.length - 1}
                    onPress={() => run({preset})}
                />
            ))}
            <Text style={[styles.groupLabel, {color: m.colors.muted}]}>{t('mail_collab_snooze_custom')}</Text>
            <View style={styles.stepper}>
                <TouchableOpacity
                    onPress={() => setIndex((value) => Math.max(0, value - 1))}
                    disabled={index <= 0}
                    style={[styles.stepBtn, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border, opacity: index <= 0 ? 0.4 : 1}]}
                    accessibilityRole="button"
                >
                    <Icon name="minus" size={20} color={m.colors.heading} />
                </TouchableOpacity>
                <Text style={[styles.stepValue, {color: m.colors.heading}]}>{label}</Text>
                <TouchableOpacity
                    onPress={() => setIndex((value) => Math.min(steps.length - 1, value + 1))}
                    disabled={index >= steps.length - 1}
                    style={[styles.stepBtn, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border, opacity: index >= steps.length - 1 ? 0.4 : 1}]}
                    accessibilityRole="button"
                >
                    <Icon name="plus" size={20} color={m.colors.heading} />
                </TouchableOpacity>
            </View>
            <View style={styles.saveRow}>
                <SheetButton primary icon="alarm-snooze" label={t('mail_collab_snooze_confirm')} disabled={busy} onPress={() => run({preset: 'in_hours', hours})} />
            </View>
        </PremiumSheet>
    );
};

const styles = StyleSheet.create({
    btn: {flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, paddingHorizontal: 14, borderRadius: 999},
    btnText: {fontSize: 14, fontWeight: '800'},
    actionsRow: {flexDirection: 'row', gap: 10, marginBottom: 12},
    saveRow: {flexDirection: 'row', marginTop: 12},
    inputWrap: {flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, borderRadius: 18, borderWidth: StyleSheet.hairlineWidth, marginBottom: 8},
    input: {flex: 1, paddingVertical: 11, fontSize: 15},
    loader: {marginVertical: 24},
    empty: {textAlign: 'center', marginVertical: 20, fontSize: 13},
    groupLabel: {fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 14, marginBottom: 4},
    tagDot: {width: 14, height: 14, borderRadius: 7},
    swatches: {flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginVertical: 6},
    swatch: {width: 28, height: 28, borderRadius: 14, borderWidth: 2},
    scopeRow: {flexDirection: 'row', gap: 8, marginTop: 8},
    scopeChip: {paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999, borderWidth: 1},
    scopeText: {fontSize: 12, fontWeight: '700'},
    currentBox: {flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderRadius: 18, borderWidth: StyleSheet.hairlineWidth, marginBottom: 8},
    currentText: {flex: 1, fontSize: 13, fontWeight: '700'},
    cancelLink: {fontSize: 12, fontWeight: '800'},
    toggleRow: {flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 18, borderWidth: StyleSheet.hairlineWidth, marginBottom: 8},
    toggleText: {flex: 1},
    toggleTitle: {fontSize: 13.5, fontWeight: '800'},
    toggleDesc: {fontSize: 11.5, marginTop: 2},
    stepper: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 6},
    stepBtn: {width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center', borderWidth: StyleSheet.hairlineWidth},
    stepValue: {fontSize: 18, fontWeight: '800'},
});
